/**
 * App-level UI state, section registry, and the render dispatcher.
 *
 * Each section lives in its own view module. Views register themselves into
 * `sectionViews` and (if they have an "+ Add" primary action) into
 * `primaryActions`. The renderer dispatches by `State.section`.
 */
import { closeModal, isModalDirty, isModalOpen } from "./components/modal";
import { toast } from "./lib/dom";
import { setupAllTables } from "./utils/table";
import { icon as svgIcon } from "./utils/icon";

export interface Section {
  id: string;
  label: string;
  icon: string;
  /** Sidebar group label - sections with the same group are clustered under
   *  a tiny uppercase heading. Leave empty for top-level (no header above). */
  group?: string;
  primary: { label: string; key: string } | null;
}

export const SECTIONS: Section[] = [
  { id: "dashboard",    label: "Dashboard",        icon: svgIcon("dashboard", { size: 18 }),     group: "",                  primary: null },
  { id: "sealed",       label: "Sealed Inventory", icon: svgIcon("package", { size: 18 }),       group: "Inventory & Sales", primary: { label: "+ Add Sealed",     key: "addSealed" } },
  { id: "cards",        label: "Cards Inventory",  icon: svgIcon("layers", { size: 18 }),        group: "Inventory & Sales", primary: { label: "+ Add Card",       key: "addCard" } },
  { id: "rips",         label: "Box Rips / Breaks", icon: svgIcon("scissors", { size: 18 }),     group: "Inventory & Sales", primary: { label: "+ New Rip",        key: "addRip" } },
  { id: "sales",        label: "Sales Log",        icon: svgIcon("dollar-sign", { size: 18 }),   group: "Inventory & Sales", primary: { label: "+ Log Sale",       key: "addSale" } },
  { id: "purchases",    label: "Purchases",        icon: svgIcon("shopping-cart", { size: 18 }), group: "Inventory & Sales", primary: { label: "+ Add Purchase",   key: "addPurchase" } },
  { id: "trades",       label: "Trades",           icon: svgIcon("arrow-swap", { size: 18 }),    group: "Inventory & Sales", primary: { label: `+ New Trade ${svgIcon("chevron-down", { size: 10 })}`, key: "addTrade" } },
  { id: "grading",      label: "Grading/Repair",   icon: svgIcon("star", { size: 18 }),          group: "Inventory & Sales", primary: { label: "+ Add Submission", key: "addGrading" } },
  { id: "transactions", label: "Adjustments",      icon: svgIcon("sliders", { size: 18 }),       group: "Books",             primary: { label: "+ Add Adjustment", key: "addTxn" } },
  { id: "expenses",     label: "Expenses",         icon: svgIcon("wallet", { size: 18 }),        group: "Books",             primary: { label: "+ Expense",        key: "addExpense" } },
  { id: "mileage",      label: "Mileage",          icon: svgIcon("route", { size: 18 }),         group: "Books",             primary: { label: "+ Trip",           key: "addMileage" } },
  { id: "employees",    label: "Employees",        icon: svgIcon("users", { size: 18 }),         group: "Books",             primary: { label: "+ Add Employee",   key: "addEmployee" } },
  { id: "buys",         label: "Buy Sheet",        icon: svgIcon("target", { size: 18 }),        group: "Tools",             primary: { label: "+ Target",         key: "addBuy" } },
  { id: "lookup",       label: "Product Lookup",   icon: svgIcon("search", { size: 18 }),        group: "Tools",             primary: null },
];

/** Mutable UI state. Avoid accumulating unrelated keys here. */
export const State: { section: string } = { section: "dashboard" };

/** Action-key → handler. View modules register here as they're ported. */
export const primaryActions: Record<string, () => void> = {};

/** Section ID → view renderer. View modules register here as they're ported. */
export const sectionViews: Record<string, (root: HTMLElement) => void> = {};

/** Called on every tab switch — views register hooks here to e.g. clear their search box. */
export const onSectionChange: Array<(prevSection: string, newSection: string) => void> = [];

/* ============================================================
   Navigation
   ============================================================ */
export function go(section: string): void {
  if (isModalDirty()) {
    if (!confirm("You have unsaved changes in the open form. Discard them and switch tabs?")) return;
  }
  if (isModalOpen()) closeModal();
  const pickerRoot = document.getElementById("pickerRoot");
  if (pickerRoot) pickerRoot.innerHTML = "";
  const prev = State.section;
  for (const cb of onSectionChange) {
    try { cb(prev, section); } catch (e) { console.error("[onSectionChange]", e); }
  }
  State.section = section;
  history.replaceState(null, "", "#" + section);
  render();
  if (window.innerWidth < 768) toggleSidebar(false);
}

export function toggleSidebar(force?: boolean): void {
  const s = document.getElementById("sidebar");
  const o = document.getElementById("sidebarOverlay");
  if (!s) return;
  let collapsed: boolean;
  if (force === true) collapsed = false;
  else if (force === false) collapsed = true;
  else collapsed = !s.classList.contains("is-collapsed");
  s.classList.toggle("is-collapsed", collapsed);
  if (window.innerWidth < 768) {
    o?.classList.toggle("open", !collapsed);
  } else {
    o?.classList.remove("open");
    localStorage.setItem("enigma_sidebar_collapsed", collapsed ? "1" : "0");
  }
}

/* ============================================================
   Render dispatch
   ============================================================ */
export function render(): void {
  // Preserve focus through full re-renders (e.g. typing in a search box).
  const ae = document.activeElement as HTMLElement | null;
  let focusInfo: { key: string; selStart: number | null; selEnd: number | null } | null = null;
  if (ae?.getAttribute("data-focus-key")) {
    const inp = ae as HTMLInputElement;
    focusInfo = {
      key: ae.getAttribute("data-focus-key")!,
      selStart: typeof inp.selectionStart === "number" ? inp.selectionStart : null,
      selEnd:   typeof inp.selectionEnd   === "number" ? inp.selectionEnd   : null,
    };
  }

  // Sidebar nav
  const nav = document.getElementById("nav");
  if (nav) {
    {
      let lastGroup = "";
      const parts: string[] = [];
      for (const sec of SECTIONS) {
        const g = sec.group || "";
        if (g && g !== lastGroup) {
          parts.push(`<div class="text-[10px] uppercase tracking-wider text-[var(--muted)] mt-3 mb-1 px-2 opacity-70">${g}</div>`);
          lastGroup = g;
        }
        parts.push(`<div class="nav-item ${sec.id === State.section ? "active" : ""}" data-section="${sec.id}"><span class="inline-flex w-5 justify-center">${sec.icon}</span><span>${sec.label}</span></div>`);
      }
      nav.innerHTML = parts.join("");
    }
    nav.querySelectorAll<HTMLElement>("[data-section]").forEach((el) => {
      el.addEventListener("click", () => go(el.getAttribute("data-section")!));
    });
  }

  // Header title + primary action
  const sec = SECTIONS.find(s => s.id === State.section);
  const title = document.getElementById("pageTitle");
  if (title && sec) title.textContent = sec.label;
  const pa = document.getElementById("primaryAction") as HTMLButtonElement | null;
  if (pa) {
    if (sec?.primary) {
      pa.innerHTML = sec.primary.label;
      pa.style.display = "";
      pa.onclick = () => {
        const handler = primaryActions[sec.primary!.key];
        if (handler) handler();
        else toast(`"${sec.primary!.label}" — coming in a later migration chunk`, "ok");
      };
    } else {
      pa.style.display = "none";
    }
  }

  // Body
  const v = document.getElementById("view");
  if (v) {
    v.innerHTML = "";
    const viewFn = sectionViews[State.section];
    if (viewFn) {
      viewFn(v);
      // Apply sort + column-visibility (show/hide + defaults) + cell-title
      // chrome to all tables in the freshly-rendered view.
      setupAllTables(v, State.section);
    } else {
      v.innerHTML = `
        <div class="panel p-8 max-w-2xl mx-auto text-center">
          <h2 class="text-lg font-semibold mb-2">${sec?.label || "Section"} — coming soon</h2>
          <p class="text-sm text-[var(--muted)]">
            This view hasn't been ported into the new project yet. The single-file app
            (<code>enigma-app-cloud.html</code>) still has it and stays usable in parallel.
            Sit tight — it'll show up in chunk 4 or 5 of the migration.
          </p>
        </div>`;
    }
  }

  // Restore focus
  if (focusInfo && v) {
    const el = v.querySelector<HTMLInputElement>(`[data-focus-key="${focusInfo.key}"]`);
    if (el) {
      el.focus();
      if (focusInfo.selStart != null) {
        try { el.setSelectionRange(focusInfo.selStart, focusInfo.selEnd); } catch { /* ignore */ }
      }
    }
  }
}

// Expose for inline onclick attributes coming from view HTML (e.g. activity-feed lot links).
(window as unknown as Record<string, unknown>).go = go;
(window as unknown as Record<string, unknown>).toggleSidebar = toggleSidebar;
(window as unknown as Record<string, unknown>).render = render;
